import React, { useEffect, useState } from "react";
import { useTheme } from "@material-ui/core";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import SoftBox from "../../../../components/SoftBox";
import SoftButton from "../../../../components/SoftButton";
import SoftInput from "../../../../components/SoftInput";
import { CompanyValuesSchema } from "../../../../form-validations";
import { UseCompanyId, UseUserId } from "../../../../hooks/auth";
import { refetchUserData } from "../../../../redux/auth/actions";
import {
  ADD_WIDGETS_TO_LIST,
  GET_WIDGETS_LIST,
} from "../../../../api/userWidgets";

const CompanyValuesEdit = ({ widget, closeModal }) => {
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const userId = UseUserId();
  const companyId = UseCompanyId();
  const dispatch = useDispatch();

  const {
    primary: { main: primaryColor },
  } = theme.palette;

  const validationOpt = {
    resolver: yupResolver(CompanyValuesSchema),
    shouldUnregister: false,
    defaultValues: {
      values: "",
    },
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm(validationOpt);

  const fetchUserWidgets = async () => {
    setLoading(true);
    try {
      const { data } = await GET_WIDGETS_LIST({ key: widget.key, companyId });
      if (data) {
        setValue("values", data[0].multilineUserInput.join("\n"));
      }
    } catch (error) {
      console.log(error.response);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUserWidgets();
  }, []);

  const triggerSave = async (formData) => {
    const { title, values } = formData;
    const multilineUserInput = values.split("\n");

    setLoading(true);
    const payload = {
      userId,
      key: widget.key,
      category: widget.category,
      title,
      multilineUserInput,
      companyId,
    };

    try {
      await ADD_WIDGETS_TO_LIST(payload);
      dispatch(refetchUserData(userId));
      setTimeout(() => {
        toast.success("Widget updated successfully");
        closeModal();
      }, 1000);
    } catch (error) {
      console.log(error.response);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(triggerSave)}>
      <SoftInput
        variant="standard"
        fullWidth
        multiline
        minRows={5}
        size="small"
        {...register("values")}
        error={!!errors?.values}
        helperText={errors?.values?.message}
        placeholder={`Enter ${widget?.title.toLowerCase()}`}
        InputProps={{ disableUnderline: true }}
        sx={{
          // light glass textarea
          border: "1px solid #E4E7EC",
          backgroundColor: "#F6F7F9 !important",
          color: "#1d2939 !important",
          "& .MuiInputBase-input::placeholder": {
            color: "#98A2B3 !important",
            opacity: "1",
          },
          "&.Mui-focused": {
            borderColor: primaryColor,
            boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
          },
        }}
      />
      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap="1rem"
        sx={{ marginTop: "2rem" }}
      >
        <SoftButton
          variant="outlined"
          color="white"
          onClick={closeModal}
          disabled={loading}
          loading={loading}
          sx={{
            width: "100%",
            borderRadius: "4px",
            // light glass secondary button
            border: "1px solid rgba(15,23,42,0.12)",
            boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
            textTransform: "none",
            background: "rgba(15,23,42,0.03)",
            color: "#475467",
            "&:hover": {
              background: "rgba(15,23,42,0.06)",
            },
          }}
        >
          Cancel
        </SoftButton>

        <SoftButton
          color="primary"
          type="submit"
          sx={{
            width: "100%",
            textTransform: "none",
            borderRadius: "4px",
            color: "#E9ECEF",
          }}
          disabled={loading}
          loading={loading}
        >
          Update
        </SoftButton>
      </SoftBox>
    </form>
  );
};

export default CompanyValuesEdit;
